import type {InitiativeProposal} from "./initiativeProposal";

/** A chat row that can carry an uncommitted initiative draft. */
type DraftMessage = {
    type: "user" | "bot";
    event_type?: string;
    initiativeProposal?: InitiativeProposal;
    /** Derived: true on every draft except the latest one in the chat. */
    initiativeSuperseded?: boolean;
};

/**
 * The review card belongs under the agent's reply, not on the tool-status row.
 * Move a proposal onto the next bot text message and clear it from the tool result.
 * Every draft except the latest is flagged superseded so only one card can be committed.
 */
export function placeInitiativeDrafts<T extends DraftMessage>(messages: T[]): T[] {
    let pendingIndex = -1;
    let changed = false;
    const next = messages.slice();
    for (let i = 0; i < next.length; i++) {
        const msg = next[i];
        if (msg.event_type === "function_response" && msg.initiativeProposal) {
            pendingIndex = i;
            continue;
        }
        if (msg.type === "user") {
            pendingIndex = -1;
            continue;
        }
        if (pendingIndex < 0 || msg.type !== "bot" || msg.event_type !== "text") continue;
        const proposal = next[pendingIndex].initiativeProposal;
        if (msg.initiativeProposal !== proposal) {
            next[i] = {...msg, initiativeProposal: proposal};
            changed = true;
        }
        next[pendingIndex] = {...next[pendingIndex], initiativeProposal: undefined};
        changed = true;
        pendingIndex = -1;
    }
    let latest = -1;
    for (let i = 0; i < next.length; i++) {
        if (next[i].initiativeProposal) latest = i;
    }
    for (let i = 0; i < next.length; i++) {
        if (!next[i].initiativeProposal) continue;
        const superseded = i !== latest;
        if (Boolean(next[i].initiativeSuperseded) === superseded) continue;
        next[i] = {...next[i], initiativeSuperseded: superseded};
        changed = true;
    }
    return changed ? next : messages;
}
